import {
    formatUnitIdRef,
    isUnitIdRef,
    normalizeUnitIdRef,
    type ContentDefinition,
    type ImportDocumentSnapshot,
    type PluginDefinition,
    type PluginOutput,
} from "@magic-md/core";
import {
    type CSSProperties,
    type FC,
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import type { RuntimeUnitInfo, ScopeVariable } from "../types/index";
import { usePluginSdkT } from "../i18n";
import { ComboSelect, type ComboSelectOption } from "./ComboSelect";
import {
    filterSelectableContentDefinitions,
    filterSelectableDefinitions,
    filterSelectableUnits,
} from "./reference-picker-helpers";
import {
    buildOutputTree,
    extractContentIdFromRef,
    extractDefIdFromRef,
    extractInputParamNameFromRef,
    formatInputParamRef,
    formatRefDisplay,
    getRefColorWithContext,
    getRefPrefix,
    getType,
    isAnyRefValue,
    type OutputNode,
    typeLabel,
} from "./refvalue-helpers";
import {
    getOutputValueStyle,
    getPluginToneStyle,
    getScopeVariableStyle,
} from "./theme-helpers";

export type ReferenceBrowserContext = {
    currentDefinitionId?: string;
    getAvailableDefinitions: () => Map<string, PluginDefinition>;
    getAvailableContentDefinitions?: () => Map<string, ContentDefinition>;
    getAvailableImportDocuments?: () => Map<string, ImportDocumentSnapshot>;
    getAvailableAssets?: () => AssetReferenceEntry[];
    getAvailableUnits?: () => RuntimeUnitInfo[];
    scopeVariables?: ScopeVariable[];
};

export interface AssetReferenceEntry {
    name: string;
    relativePath: string;
    isDirectory: boolean;
    namespace?: string;
}

export interface ReferenceBrowserProps {
    value?: string;
    onChange: (value: string) => void;
    context: ReferenceBrowserContext;
    label?: string;
    hint?: string;
    borderless?: boolean;
    inline?: boolean;
}

const interactiveRowClass =
    "flex items-center gap-1 py-0.5 px-1 rounded hover:bg-muted/40 cursor-pointer group text-[11px] text-left w-full";

type ReferenceTargetKind = "input" | "scope" | "definition" | "content" | "import-document" | "asset" | "unit";

type ReferenceTarget = {
    kind: ReferenceTargetKind;
    id: string;
};

const targetValue = (kind: ReferenceTargetKind, id: string) => `${kind}:${id}`;

function parseTargetValue(value: string): ReferenceTarget | undefined {
    const [kind, ...idParts] = value.split(":");
    const id = idParts.join(":");
    if (
        (kind === "input" ||
            kind === "scope" ||
            kind === "definition" ||
            kind === "content" ||
            kind === "import-document" ||
            kind === "asset" ||
            kind === "unit") &&
        id
    ) {
        return { kind, id };
    }
    return undefined;
}

function useSyncedSelection(initialValue?: string) {
    const [selectedValue, setSelectedValue] = useState<string>(
        initialValue ?? "",
    );

    useEffect(() => {
        setSelectedValue(initialValue ?? "");
    }, [initialValue]);

    return [selectedValue, setSelectedValue] as const;
}

function runOnEnterOrSpace(
    event: ReactKeyboardEvent<HTMLElement>,
    action: () => void,
) {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        action();
    }
}

const InteractiveRow: FC<{
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    onTrigger: () => void;
    expanded?: boolean;
    hasPopup?: boolean;
    secondaryAction?: () => void;
}> = ({
    children,
    className,
    style,
    onTrigger,
    expanded,
    hasPopup,
    secondaryAction,
}) => (
        <div className={className} style={style}>
            <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-1 text-left"
                onClick={onTrigger}
                onKeyDown={(event) => runOnEnterOrSpace(event, onTrigger)}
                aria-expanded={expanded}
                aria-haspopup={hasPopup ? "dialog" : undefined}
            >
                {children}
            </button>
            {secondaryAction ? (
                <button
                    type="button"
                    className="ml-auto opacity-0 group-hover:opacity-100 px-1.5 py-0 rounded text-[9px] font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-opacity shrink-0"
                    onClick={secondaryAction}
                >
                    Use
                </button>
            ) : null}
        </div>
    );

const ExpandableTreeRow: FC<{
    depth?: number;
    style?: CSSProperties;
    expanded: boolean;
    hasChildren: boolean;
    onTrigger: () => void;
    onUse?: () => void;
    children: ReactNode;
}> = ({ depth, style, expanded, hasChildren, onTrigger, onUse, children }) => (
    <div
        style={depth !== undefined ? { marginLeft: depth * 12, ...style } : style}
    >
        <InteractiveRow
            className={interactiveRowClass}
            onTrigger={onTrigger}
            expanded={hasChildren ? expanded : undefined}
            secondaryAction={hasChildren ? onUse : undefined}
        >
            {hasChildren ? (
                <span className="w-3 shrink-0 text-center text-[10px] text-muted-foreground">
                    {expanded ? "▼" : "▶"}
                </span>
            ) : (
                <span className="w-3 shrink-0" />
            )}
            {children}
        </InteractiveRow>
    </div>
);

const TreeNodeItem: FC<{
    node: OutputNode;
    defId: string;
    onSelect: (refPath: string) => void;
    depth: number;
}> = ({ node, defId, onSelect, depth }) => {
    const [expanded, setExpanded] = useState(false);
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const refPath = `$ref{${defId}.output${node.path ? `.${node.path}` : ""}}`;

    const handleTrigger = () => {
        if (hasChildren) {
            setExpanded(!expanded);
        } else {
            onSelect(refPath);
        }
    };

    return (
        <>
            <ExpandableTreeRow
                depth={depth}
                expanded={expanded}
                hasChildren={hasChildren}
                onTrigger={handleTrigger}
                onUse={() => onSelect(refPath)}
            >
                <span className="font-mono font-medium text-foreground/80">
                    {node.key}
                </span>
                <span
                    className="font-mono truncate"
                    style={getOutputValueStyle(node.type)}
                >
                    {typeLabel(node.type, node.value)}
                </span>
            </ExpandableTreeRow>

            {expanded && hasChildren ? (
                <div>
                    {children.map((child) => (
                        <TreeNodeItem
                            key={child.path}
                            node={child}
                            defId={defId}
                            onSelect={onSelect}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            ) : null}
        </>
    );
};

const DefinitionBrowser: FC<{
    definition?: PluginDefinition;
    onSelect: (refPath: string) => void;
}> = ({ definition: selectedDef, onSelect }) => {
    const t = usePluginSdkT();
    const selectedOutput = selectedDef?.output;
    const visibleOutputKeys = selectedOutput
        ? Object.keys(selectedOutput).filter((key) => !key.startsWith("__"))
        : [];
    const hasOutput = visibleOutputKeys.length > 0;
    const outputKeyCount = visibleOutputKeys.length;

    const tree = useMemo(
        () => (selectedOutput ? buildOutputTree(selectedOutput, "") : []),
        [selectedOutput],
    );

    if (!selectedDef) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {t("referenceBrowser.selectDefinition")}
            </div>
        );
    }

    return (
        <div className="max-h-56 overflow-auto border-t border-border/50">
            {hasOutput ? (
                <div className="pb-1">
                    <InteractiveRow
                        className={interactiveRowClass}
                        style={{ marginLeft: 4 }}
                        onTrigger={() => onSelect(`$ref{${selectedDef.id}.output}`)}
                    >
                        <span
                            className="w-3 shrink-0 text-center text-[10px]"
                            style={getPluginToneStyle("accent")}
                        >
                            📦
                        </span>
                        <span
                            className="font-mono font-semibold"
                            style={getPluginToneStyle("accent")}
                        >
                            output
                        </span>
                        <span
                            className="font-mono text-[10px] truncate"
                            style={getPluginToneStyle("accent", "soft")}
                        >
                            Object({outputKeyCount})
                        </span>
                    </InteractiveRow>

                    {tree.map((node) => (
                        <TreeNodeItem
                            key={node.path}
                            node={node}
                            defId={selectedDef.id}
                            onSelect={onSelect}
                            depth={1}
                        />
                    ))}
                </div>
            ) : (
                <div className="px-4 py-2 text-[10px] italic text-muted-foreground">
                    Run this plugin first to see its output fields
                </div>
            )}
        </div>
    );
};

const ContentBrowser: FC<{
    content?: ContentDefinition;
    onSelect: (refPath: string) => void;
}> = ({ content: selectedContent, onSelect }) => {
    const t = usePluginSdkT();
    const innerEntries = useMemo(
        () => collectVisibleContentOutputEntries(selectedContent?.output),
        [selectedContent?.output],
    );

    if (!selectedContent) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {t("referenceBrowser.selectContent")}
            </div>
        );
    }

    return (
        <div className="max-h-56 overflow-auto border-t border-border/50">
            <InteractiveRow
                className={interactiveRowClass}
                style={{ marginLeft: 4 }}
                onTrigger={() => onSelect(`$ref{${selectedContent.id}.body}`)}
            >
                <span
                    className="w-3 shrink-0 text-center text-[10px]"
                    style={getPluginToneStyle("accent")}
                >
                    ¶
                </span>
                <span
                    className="font-mono font-semibold"
                    style={getPluginToneStyle("accent")}
                >
                    body
                </span>
                <span
                    className="font-mono text-[10px] truncate"
                    style={getPluginToneStyle("accent", "soft")}
                >
                    string({selectedContent.body.length} chars)
                </span>
            </InteractiveRow>

            {innerEntries.length > 0 ? (
                <div className="pb-1">
                    <InteractiveRow
                        className={interactiveRowClass}
                        style={{ marginLeft: 4 }}
                        onTrigger={() => onSelect(`$ref{${selectedContent.id}.output}`)}
                    >
                        <span
                            className="w-3 shrink-0 text-center text-[10px]"
                            style={getPluginToneStyle("info")}
                        >
                            📝
                        </span>
                        <span
                            className="font-mono font-semibold"
                            style={getPluginToneStyle("info")}
                        >
                            output
                        </span>
                        <span
                            className="font-mono text-[10px] truncate"
                            style={getPluginToneStyle("info", "soft")}
                        >
                            Object({innerEntries.length})
                        </span>
                    </InteractiveRow>

                    {innerEntries.map(([innerDefId, innerOutput]) => (
                        <ContentInnerDefItem
                            key={innerDefId}
                            contentId={selectedContent.id}
                            innerDefId={innerDefId}
                            output={innerOutput}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            ) : (
                <div className="px-4 py-2 text-[10px] italic text-muted-foreground">
                    {t("referenceBrowser.runContentForOutput")}
                </div>
            )}
        </div>
    );
};

const ContentInnerDefItem: FC<{
    contentId: string;
    innerDefId: string;
    output: unknown;
    onSelect: (refPath: string) => void;
}> = ({ contentId, innerDefId, output, onSelect }) => {
    const [expanded, setExpanded] = useState(false);
    const refId = `${contentId}/${innerDefId}`;
    const nestedEntries = useMemo(
        () => isContentOutputValue(output) ? collectVisibleContentOutputEntries(output) : [],
        [output],
    );
    const tree = useMemo(
        () => nestedEntries.length > 0 ? [] : buildOutputTree(output, "", 4, 0),
        [nestedEntries.length, output],
    );
    const hasNestedEntries = nestedEntries.length > 0;
    const hasChildren = hasNestedEntries || tree.length > 0;
    const outputLabel = hasNestedEntries
        ? `content · Object(${nestedEntries.length})`
        : `output · ${typeLabel(getType(output), output)}`;

    return (
        <>
            <ExpandableTreeRow
                style={{ marginLeft: 8 }}
                expanded={expanded}
                hasChildren={hasChildren}
                onTrigger={() => {
                    if (hasChildren) setExpanded(!expanded);
                    else onSelect(`$ref{${refId}.output}`);
                }}
                onUse={() => onSelect(`$ref{${refId}.output}`)}
            >
                <span
                    className="font-mono font-medium"
                    style={getPluginToneStyle("info")}
                >
                    {innerDefId}
                </span>
                <span
                    className="font-mono text-[10px] truncate"
                    style={getPluginToneStyle("info", "soft")}
                >
                    {outputLabel}
                </span>
            </ExpandableTreeRow>

            {expanded && hasChildren ? (
                <div>
                    {hasNestedEntries
                        ? nestedEntries.map(([nestedId, nestedOutput]) => (
                            <ContentInnerDefItem
                                key={nestedId}
                                contentId={refId}
                                innerDefId={nestedId}
                                output={nestedOutput}
                                onSelect={onSelect}
                            />
                        ))
                        : tree.map((node) => (
                            <TreeNodeItem
                                key={node.path}
                                node={node}
                                defId={refId}
                                onSelect={onSelect}
                                depth={2}
                            />
                        ))}
                </div>
            ) : null}
        </>
    );
};

function isOutputRecord(output: unknown): output is Record<string, unknown> {
    return typeof output === "object" && output !== null && !Array.isArray(output);
}

function isContentOutputValue(
    output: unknown,
): output is NonNullable<ContentDefinition["output"]> {
    if (!isOutputRecord(output)) return false;
    const metadata = output.__metadata;
    return isOutputRecord(metadata) && metadata.unitType === "content";
}

function collectVisibleContentOutputEntries(
    output: ContentDefinition["output"] | undefined,
): Array<[string, unknown]> {
    if (!output) return [];
    return Object.entries(output).filter(([key]) => !key.startsWith("__"));
}

const UnitIdentityBrowser: FC<{
    units: RuntimeUnitInfo[];
    selectedUnitId?: string;
    onSelect: (refPath: string) => void;
}> = ({ units, selectedUnitId, onSelect }) => {
    const t = usePluginSdkT();
    if (units.length === 0) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {t("referenceBrowser.noUnits")}
            </div>
        );
    }

    return (
        <div className="max-h-56 overflow-auto border-t border-border/50 py-0.5">
            {units.map((unit) => {
                const selected = selectedUnitId === unit.id;
                return (
                    <InteractiveRow
                        key={`${unit.kind}:${unit.id}`}
                        className={`${interactiveRowClass}${selected ? " bg-muted/50" : ""}`}
                        onTrigger={() => onSelect(formatUnitIdRef(unit.id))}
                    >
                        <span
                            className="w-3 shrink-0 text-center text-[10px]"
                            style={getScopeVariableStyle("purple")}
                        >
                            ⎇
                        </span>
                        <span
                            className="font-mono font-medium"
                            style={getScopeVariableStyle("purple")}
                        >
                            {unit.id}
                        </span>
                        <span className="font-mono text-[10px] truncate text-muted-foreground">
                            {unit.pluginName ? `${unit.kind} · ${unit.pluginName}` : unit.kind}
                        </span>
                    </InteractiveRow>
                );
            })}
        </div>
    );
};

function getImportDocumentLabel(
    namespace: string,
    snapshot: ImportDocumentSnapshot,
): string {
    const source = snapshot.resolvedPath ?? snapshot.source;
    const fileName = source?.split(/[\\/]/).filter(Boolean).at(-1);
    return fileName ? `${namespace} · ${fileName}` : namespace;
}

function getVisibleOutputKeys(output: PluginOutput | undefined): string[] {
    return output
        ? Object.keys(output).filter((key) => !key.startsWith("__"))
        : [];
}

const ImportDocumentDefinitionItem: FC<{
    namespace: string;
    localId: string;
    definition: PluginDefinition;
    output?: PluginOutput;
    onSelect: (refPath: string) => void;
}> = ({ namespace, localId, definition, output, onSelect }) => {
    const [expanded, setExpanded] = useState(false);
    const visibleOutputKeys = getVisibleOutputKeys(output);
    const hasOutput = visibleOutputKeys.length > 0;
    const tree = useMemo(
        () => (output ? buildOutputTree(output, "") : []),
        [output],
    );
    const refId = `${namespace}/${localId}`;

    return (
        <div>
            <ExpandableTreeRow
                expanded={expanded}
                hasChildren={hasOutput}
                onTrigger={() => {
                    if (hasOutput) setExpanded(!expanded);
                }}
                onUse={hasOutput ? () => onSelect(`$ref{${refId}.output}`) : undefined}
            >
                <span
                    className="font-mono font-medium"
                    style={getPluginToneStyle("accent")}
                >
                    {localId}
                </span>
                <span className="font-mono text-[10px] truncate text-muted-foreground">
                    {definition.pluginName}
                </span>
                {hasOutput ? (
                    <span
                        className="font-mono text-[10px] truncate"
                        style={getPluginToneStyle("accent", "soft")}
                    >
                        Object({visibleOutputKeys.length})
                    </span>
                ) : null}
            </ExpandableTreeRow>

            {expanded && hasOutput ? (
                <div>
                    <InteractiveRow
                        className={interactiveRowClass}
                        style={{ marginLeft: 16 }}
                        onTrigger={() => onSelect(`$ref{${refId}.output}`)}
                    >
                        <span
                            className="w-3 shrink-0 text-center text-[10px]"
                            style={getPluginToneStyle("accent")}
                        >
                            📦
                        </span>
                        <span
                            className="font-mono font-semibold"
                            style={getPluginToneStyle("accent")}
                        >
                            output
                        </span>
                    </InteractiveRow>
                    {tree.map((node) => (
                        <TreeNodeItem
                            key={node.path}
                            node={node}
                            defId={refId}
                            onSelect={onSelect}
                            depth={2}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const ImportDocumentContentItem: FC<{
    namespace: string;
    localId: string;
    content: ContentDefinition;
    onSelect: (refPath: string) => void;
}> = ({ namespace, localId, content, onSelect }) => {
    const [expanded, setExpanded] = useState(false);
    const refId = `${namespace}/${localId}`;
    const innerEntries = useMemo(() => {
        if (!content.output) return [];
        return Object.entries(content.output).filter(
            ([key]) => !key.startsWith("__"),
        );
    }, [content.output]);
    const hasChildren = innerEntries.length > 0;

    return (
        <div>
            <ExpandableTreeRow
                expanded={expanded}
                hasChildren={hasChildren}
                onTrigger={() => {
                    if (hasChildren) setExpanded(!expanded);
                    else onSelect(`$ref{${refId}.body}`);
                }}
                onUse={() => onSelect(`$ref{${refId}.body}`)}
            >
                <span
                    className="font-mono font-medium"
                    style={getPluginToneStyle("info")}
                >
                    {localId}
                </span>
                <span
                    className="font-mono text-[10px] truncate"
                    style={getPluginToneStyle("info", "soft")}
                >
                    body · string({content.body.length} chars)
                </span>
            </ExpandableTreeRow>

            {expanded && hasChildren ? (
                <div>
                    <InteractiveRow
                        className={interactiveRowClass}
                        style={{ marginLeft: 16 }}
                        onTrigger={() => onSelect(`$ref{${refId}.body}`)}
                    >
                        <span
                            className="w-3 shrink-0 text-center text-[10px]"
                            style={getPluginToneStyle("info")}
                        >
                            ¶
                        </span>
                        <span
                            className="font-mono font-semibold"
                            style={getPluginToneStyle("info")}
                        >
                            body
                        </span>
                    </InteractiveRow>
                    {innerEntries.map(([innerDefId, innerOutput]) => (
                        <ContentInnerDefItem
                            key={innerDefId}
                            contentId={refId}
                            innerDefId={innerDefId}
                            output={innerOutput}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const ImportDocumentBrowser: FC<{
    namespace: string;
    snapshot?: ImportDocumentSnapshot;
    onSelect: (refPath: string) => void;
}> = ({ namespace, snapshot, onSelect }) => {
    const t = usePluginSdkT();
    if (!snapshot) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {t("referenceBrowser.selectImportDocument")}
            </div>
        );
    }

    const definitions = Object.entries(snapshot.definitions ?? {});
    const contentDefinitions = Object.entries(snapshot.contentDefinitions ?? {});
    const hasEntries = definitions.length > 0 || contentDefinitions.length > 0;

    return (
        <div className="max-h-56 overflow-auto border-t border-border/50 py-0.5">
            <div className="px-3 py-1 text-[10px] font-medium text-muted-foreground">
                {getImportDocumentLabel(namespace, snapshot)}
            </div>
            {!hasEntries ? (
                <div className="px-3 py-2 text-[10px] italic text-muted-foreground">
                    {t("referenceBrowser.emptyImportDocument")}
                </div>
            ) : null}
            {definitions.map(([localId, definition]) => (
                <ImportDocumentDefinitionItem
                    key={`definition:${localId}`}
                    namespace={namespace}
                    localId={localId}
                    definition={definition}
                    output={snapshot.outputs?.[localId] ?? definition.output}
                    onSelect={onSelect}
                />
            ))}
            {contentDefinitions.map(([localId, content]) => (
                <ImportDocumentContentItem
                    key={`content:${localId}`}
                    namespace={namespace}
                    localId={localId}
                    content={{
                        ...content,
                        output: snapshot.contentOutputs?.[localId] ?? content.output,
                    }}
                    onSelect={onSelect}
                />
            ))}
        </div>
    );
};

interface AssetTreeNode {
    name: string;
    relativePath: string;
    isDirectory: boolean;
    children: AssetTreeNode[];
}

function buildAssetTree(entries: AssetReferenceEntry[]): AssetTreeNode[] {
    const root: AssetTreeNode[] = [];
    const byPath = new Map<string, AssetTreeNode>();

    for (const entry of entries) {
        const segments = entry.relativePath.split("/").filter(Boolean);
        if (segments.length === 0) continue;
        let current = root;
        let path = "";
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            path = path ? `${path}/${segment}` : segment;
            let node = byPath.get(path);
            if (!node) {
                node = {
                    name: segment,
                    relativePath: path,
                    isDirectory: i < segments.length - 1 || entry.isDirectory,
                    children: [],
                };
                byPath.set(path, node);
                current.push(node);
            }
            current = node.children;
        }
    }

    const sortNodes = (nodes: AssetTreeNode[]) => {
        nodes.sort((a, b) => {
            if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        for (const node of nodes) sortNodes(node.children);
    };
    sortNodes(root);
    return root;
}

const AssetTreeItem: FC<{
    node: AssetTreeNode;
    depth: number;
    namespace?: string;
    onSelect: (refPath: string) => void;
}> = ({ node, depth, namespace, onSelect }) => {
    const [expanded, setExpanded] = useState(false);
    const hasChildren = node.children.length > 0;
    const refPrefix = namespace ? `${namespace}/assets` : "assets";
    const refPath = `$ref{${refPrefix}/${node.relativePath}}`;
    return (
        <>
            <ExpandableTreeRow
                depth={depth}
                expanded={expanded}
                hasChildren={hasChildren}
                onTrigger={() => {
                    if (hasChildren) setExpanded(!expanded);
                    else onSelect(refPath);
                }}
                onUse={() => onSelect(refPath)}
            >
                <span className="font-mono font-medium text-foreground/80">
                    {node.isDirectory ? "📁" : "📄"} {node.name}
                </span>
                <span className="font-mono text-[10px] truncate text-muted-foreground">
                    {refPrefix}/{node.relativePath}
                </span>
            </ExpandableTreeRow>
            {expanded && hasChildren ? (
                <div>
                    {node.children.map((child) => (
                        <AssetTreeItem
                            key={child.relativePath}
                            node={child}
                            depth={depth + 1}
                            namespace={namespace}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            ) : null}
        </>
    );
};

const AssetBrowser: FC<{
    entries: AssetReferenceEntry[];
    namespace?: string;
    onSelect: (refPath: string) => void;
}> = ({ entries, namespace, onSelect }) => {
    const t = usePluginSdkT();
    const scopedEntries = useMemo(
        () => entries.filter((entry) => (entry.namespace ?? "") === (namespace ?? "")),
        [entries, namespace],
    );
    const tree = useMemo(() => buildAssetTree(scopedEntries), [scopedEntries]);
    if (tree.length === 0) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {namespace
                    ? t("referenceBrowser.noImportAssets")
                    : t("referenceBrowser.noDocumentAssets")}
            </div>
        );
    }
    return (
        <div className="max-h-56 overflow-auto border-t border-border/50 py-0.5">
            {tree.map((node) => (
                <AssetTreeItem
                    key={node.relativePath}
                    node={node}
                    depth={0}
                    namespace={namespace}
                    onSelect={onSelect}
                />
            ))}
        </div>
    );
};

const ScopeTreeNode: FC<{
    node: OutputNode;
    onSelect: (subPath: string) => void;
    depth: number;
}> = ({ node, onSelect, depth }) => {
    const [expanded, setExpanded] = useState(false);
    const children = node.children ?? [];
    const hasChildren = children.length > 0;

    const handleTrigger = () => {
        if (hasChildren) {
            setExpanded(!expanded);
        } else {
            onSelect(node.path);
        }
    };

    return (
        <>
            <ExpandableTreeRow
                depth={depth}
                expanded={expanded}
                hasChildren={hasChildren}
                onTrigger={handleTrigger}
                onUse={() => onSelect(node.path)}
            >
                <span className="font-mono font-medium text-foreground/80">
                    {node.key}
                </span>
                <span
                    className="font-mono truncate"
                    style={getOutputValueStyle(node.type)}
                >
                    {typeLabel(node.type, node.value)}
                </span>
            </ExpandableTreeRow>

            {expanded && hasChildren ? (
                <div>
                    {children.map((child) => (
                        <ScopeTreeNode
                            key={child.path}
                            node={child}
                            onSelect={onSelect}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            ) : null}
        </>
    );
};

const ScopeVariableItem: FC<{
    variable: ScopeVariable;
    onSelect: (refStr: string) => void;
}> = ({ variable, onSelect }) => {
    const t = usePluginSdkT();
    const [expanded, setExpanded] = useState(false);

    const tree = useMemo(() => {
        if (!variable.browsable || variable.sampleValue == null) return [];
        return buildOutputTree(variable.sampleValue, "", 4, 0);
    }, [variable.browsable, variable.sampleValue]);

    const buildRef = (subPath?: string): string => {
        if (!subPath) return variable.refTemplate;
        const base = variable.refTemplate;
        if (base.startsWith("$ref{")) {
            const inner = base.slice(5, -1);
            const separator = subPath.startsWith("[") ? "" : ".";
            return `$ref{${inner}${separator}${subPath}}`;
        }
        return base;
    };

    if (!variable.browsable) {
        return (
            <InteractiveRow
                className="flex items-center gap-1.5 py-0.5 px-3 cursor-pointer hover:bg-muted/40 text-[11px] group text-left w-full"
                onTrigger={() => onSelect(variable.refTemplate)}
            >
                <span
                    className="font-mono font-medium"
                    style={getScopeVariableStyle(variable.color)}
                >
                    {variable.displayName}
                </span>
                {variable.sampleValue !== undefined ? (
                    <span className="font-mono text-[10px] text-muted-foreground truncate">
                        {JSON.stringify(variable.sampleValue).slice(0, 40)}
                    </span>
                ) : null}
                {variable.origin.type === "for" && variable.origin.itemCount != null ? (
                    <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">
                        ×{variable.origin.itemCount}
                    </span>
                ) : null}
            </InteractiveRow>
        );
    }

    return (
        <div>
            <ExpandableTreeRow
                expanded={expanded}
                hasChildren={tree.length > 0}
                onTrigger={() => setExpanded(!expanded)}
                onUse={() => onSelect(variable.refTemplate)}
            >
                <span
                    className="font-mono font-semibold"
                    style={getScopeVariableStyle(variable.color)}
                >
                    {variable.displayName}
                </span>
                {variable.origin.type === "for" && variable.origin.itemCount != null ? (
                    <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">
                        ×{variable.origin.itemCount}
                    </span>
                ) : null}
            </ExpandableTreeRow>

            {expanded && tree.length > 0 ? (
                <div className="ml-4">
                    {tree.map((node) => (
                        <ScopeTreeNode
                            key={node.path}
                            node={node}
                            onSelect={(subPath) => onSelect(buildRef(subPath))}
                            depth={1}
                        />
                    ))}
                </div>
            ) : null}

            {expanded && tree.length === 0 && variable.sampleValue === undefined ? (
                <div className="px-4 py-1 text-[10px] italic text-muted-foreground">
                    {t("referenceBrowser.runSourceForFields")}
                </div>
            ) : null}
        </div>
    );
};

const ScopeVariableBrowser: FC<{
    variable?: ScopeVariable;
    onSelect: (refStr: string) => void;
}> = ({ variable, onSelect }) => {
    const t = usePluginSdkT();
    if (!variable) {
        return (
            <div className="px-3 py-2 text-[10px] text-muted-foreground">
                {t("referenceBrowser.selectScopeVariable")}
            </div>
        );
    }

    return (
        <div className="max-h-56 overflow-auto border-t border-border/50 py-0.5">
            <ScopeVariableItem variable={variable} onSelect={onSelect} />
        </div>
    );
};

const inputParamNamePattern = /^\w[\w-]*$/;

function normalizeInputParamDraft(value: string): string {
    const trimmed = value.trim();
    if (trimmed.startsWith("$ref{input/") && trimmed.endsWith("}")) {
        return trimmed.slice("$ref{input/".length, -1);
    }
    if (trimmed.startsWith("input/")) {
        return trimmed.slice("input/".length);
    }
    return trimmed;
}

const InputParamBrowser: FC<{
    initialParamName?: string;
    inputVariables?: ScopeVariable[];
    onSelect: (refStr: string) => void;
}> = ({ initialParamName, inputVariables = [], onSelect }) => {
    const t = usePluginSdkT();
    const [paramName, setParamName] = useState(initialParamName ?? "");

    useEffect(() => {
        setParamName(initialParamName ?? "");
    }, [initialParamName]);

    const normalizedParamName = normalizeInputParamDraft(paramName);
    const isValid = inputParamNamePattern.test(normalizedParamName);

    const commit = () => {
        if (!isValid) return;
        onSelect(formatInputParamRef(normalizedParamName));
    };

    return (
        <div className="border-t border-border/50 px-3 py-2">
            {inputVariables.length > 0 ? (
                <div className="-mx-3 -mt-2 mb-2 border-b border-border/50 py-1">
                    <div className="px-3 py-1 text-[10px] font-medium text-muted-foreground">
                        {t("referenceBrowser.declaredInputs")}
                    </div>
                    {inputVariables.map((variable) => (
                        <ScopeVariableItem
                            key={variable.displayName}
                            variable={variable}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            ) : null}
            <label className="block text-[10px] font-medium text-muted-foreground">
                {t("referenceBrowser.paramName")}
            </label>
            <div className="mt-1 flex items-center gap-1.5">
                <span
                    className="shrink-0 rounded border border-border bg-muted/30 px-1.5 py-1 text-[10px] font-mono"
                    style={getScopeVariableStyle("blue")}
                >
                    input/
                </span>
                <input
                    className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-mono outline-none focus:border-primary focus:ring-1 focus:ring-primary/30"
                    value={paramName}
                    onChange={(event) => setParamName(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            commit();
                        }
                    }}
                    placeholder="name"
                />
                <button
                    type="button"
                    className="shrink-0 rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={commit}
                    disabled={!isValid}
                >
                    {t("referenceBrowser.use")}
                </button>
            </div>
            {paramName.trim() && !isValid ? (
                <p className="mt-1 text-[10px] text-destructive">
                    {t("referenceBrowser.invalidParamName")}
                </p>
            ) : null}
        </div>
    );
};

function findImportDocumentNamespaceForRef(
    refStr: string,
    importDocuments: Map<string, ImportDocumentSnapshot>,
): string | undefined {
    const defId = extractDefIdFromRef(refStr);
    if (!defId) return undefined;
    const slashIdx = defId.indexOf("/");
    if (slashIdx <= 0) return undefined;
    const namespace = defId.slice(0, slashIdx);
    const localId = defId.slice(slashIdx + 1);
    const snapshot = importDocuments.get(namespace);
    if (!snapshot) return undefined;
    if (snapshot.definitions?.[localId]) return namespace;
    if (snapshot.outputs?.[localId]) return namespace;
    if (snapshot.contentDefinitions?.[localId]) return namespace;
    if (snapshot.contentOutputs?.[localId]) return namespace;
    return undefined;
}

function findAssetNamespaceForRef(refStr: string): string | undefined {
    if (!refStr.startsWith("$ref{")) return undefined;
    const inner = refStr.slice(5, -1);
    const match = inner.match(/^([\w-]+)\/assets\//);
    return match?.[1];
}

export const ReferenceBrowser: FC<ReferenceBrowserProps> = ({
    value,
    onChange,
    context,
    label = "",
    hint,
    borderless,
    inline,
}) => {
    const t = usePluginSdkT();
    const [showBrowser, setShowBrowser] = useState(false);

    const definitions = useMemo(() => {
        return filterSelectableDefinitions(
            context.getAvailableDefinitions(),
            context.currentDefinitionId,
        );
    }, [context]);

    const contentDefinitions = useMemo(() => {
        if (!context.getAvailableContentDefinitions) {
            return new Map<string, ContentDefinition>();
        }
        return filterSelectableContentDefinitions(
            context.getAvailableContentDefinitions(),
            context.currentDefinitionId,
        );
    }, [context]);

    const importDocuments = useMemo(() => {
        return context.getAvailableImportDocuments?.() ?? new Map<string, ImportDocumentSnapshot>();
    }, [context]);

    const units = useMemo(
        () => filterSelectableUnits(
            context.getAvailableUnits?.() ?? [],
            context.currentDefinitionId,
        ).sort((a, b) => a.id.localeCompare(b.id)),
        [context],
    );
    const assets = context.getAvailableAssets?.() ?? [];
    const assetNamespaces = useMemo(
        () => Array.from(
            new Set(
                assets
                    .map((entry) => entry.namespace)
                    .filter((namespace): namespace is string => !!namespace),
            ),
        ).sort((a, b) => a.localeCompare(b)),
        [assets],
    );
    const scopeVars = context.scopeVariables ?? [];
    const inputScopeVars = useMemo(
        () => scopeVars.filter((variable) => variable.displayName.startsWith("input/")),
        [scopeVars],
    );
    const otherScopeVars = useMemo(
        () => scopeVars.filter((variable) => !variable.displayName.startsWith("input/")),
        [scopeVars],
    );
    const currentRefStr = isAnyRefValue(value) ? value : undefined;
    const currentColor = currentRefStr
        ? getRefColorWithContext(currentRefStr, contentDefinitions)
        : "amber";

    const targetOptions = useMemo<ComboSelectOption[]>(() => [
        {
            value: targetValue("input", "param"),
            label: t("referenceBrowser.inputParam"),
            group: t("referenceBrowser.group.templateInputs"),
        },
        ...inputScopeVars.map((variable) => ({
            value: targetValue("scope", variable.displayName),
            label: variable.displayName,
            group: t("referenceBrowser.group.templateInputs"),
        })),
        ...otherScopeVars.map((variable) => ({
            value: targetValue("scope", variable.displayName),
            label: variable.displayName,
            group: t("referenceBrowser.group.scopeVariables"),
        })),
        ...Array.from(definitions.values()).map((definition) => ({
            value: targetValue("definition", definition.id),
            label: definition.id,
            group: t("referenceBrowser.group.documentRefs"),
        })),
        ...Array.from(contentDefinitions.values()).map((definition) => ({
            value: targetValue("content", definition.id),
            label: definition.id,
            group: t("referenceBrowser.group.contentRefs"),
        })),
        ...Array.from(importDocuments.entries()).map(([namespace, snapshot]) => ({
            value: targetValue("import-document", namespace),
            label: getImportDocumentLabel(namespace, snapshot),
            group: t("referenceBrowser.group.importDocuments"),
        })),
        ...units.map((unit) => ({
            value: targetValue("unit", unit.id),
            label: unit.id,
            group: t("referenceBrowser.group.unitIdentity"),
        })),
        ...(context.getAvailableAssets
            ? [{
                value: targetValue("asset", "assets"),
                label: "assets",
                group: "🗂 Assets",
            }]
            : []),
        ...assetNamespaces.map((namespace) => ({
            value: targetValue("asset", `${namespace}/assets`),
            label: `${namespace}/assets`,
            group: "🗂 Assets",
        })),
    ], [inputScopeVars, otherScopeVars, definitions, contentDefinitions, importDocuments, units, context.getAvailableAssets, assetNamespaces, t]);

    const initialTargetValue = useMemo(() => {
        if (!value) return "";

        const matchingScope = scopeVars.find(
            (variable) => variable.refTemplate === value,
        );
        if (matchingScope) {
            return targetValue("scope", matchingScope.displayName);
        }

        const unitId = isUnitIdRef(value) ? normalizeUnitIdRef(value) : undefined;
        if (unitId && units.some((unit) => unit.id === unitId)) {
            return targetValue("unit", unitId);
        }

        const inputParamName = extractInputParamNameFromRef(value);
        if (inputParamName !== undefined) {
            return targetValue("input", "param");
        }

        const defId = extractDefIdFromRef(value);
        const importDocumentNamespace = findImportDocumentNamespaceForRef(
            value,
            importDocuments,
        );
        if (importDocumentNamespace) {
            return targetValue("import-document", importDocumentNamespace);
        }

        if (defId && definitions.has(defId)) {
            return targetValue("definition", defId);
        }

        const contentId = extractContentIdFromRef(value, contentDefinitions);
        if (contentId && contentDefinitions.has(contentId)) {
            return targetValue("content", contentId);
        }

        const assetNamespace = findAssetNamespaceForRef(value);
        if (assetNamespace) {
            return targetValue("asset", `${assetNamespace}/assets`);
        }

        if (value.startsWith("$ref{assets/")) {
            return targetValue("asset", "assets");
        }

        return "";
    }, [value, scopeVars, definitions, contentDefinitions, importDocuments, units]);

    const [selectedTarget, setSelectedTarget] =
        useSyncedSelection(initialTargetValue);
    const selectedTargetInfo = parseTargetValue(selectedTarget);
    const selectedScopeVariable =
        selectedTargetInfo?.kind === "scope"
            ? scopeVars.find((variable) => variable.displayName === selectedTargetInfo.id)
            : undefined;
    const selectedDefinition =
        selectedTargetInfo?.kind === "definition"
            ? definitions.get(selectedTargetInfo.id)
            : undefined;
    const selectedContent =
        selectedTargetInfo?.kind === "content"
            ? contentDefinitions.get(selectedTargetInfo.id)
            : undefined;
    const selectedImportDocument =
        selectedTargetInfo?.kind === "import-document"
            ? importDocuments.get(selectedTargetInfo.id)
            : undefined;
    const selectedUnit =
        selectedTargetInfo?.kind === "unit"
            ? units.find((unit) => unit.id === selectedTargetInfo.id)
            : undefined;
    const selectedInputParamName = extractInputParamNameFromRef(value);

    const handleSelectRef = (refPath: string) => {
        onChange(refPath);
        setShowBrowser(false);
    };

    const handleTargetChange = (nextTarget: string) => {
        setSelectedTarget(nextTarget);
        const next = parseTargetValue(nextTarget);
        if (next?.kind === "scope") {
            const variable = scopeVars.find((item) => item.displayName === next.id);
            if (variable && !variable.browsable) {
                handleSelectRef(variable.refTemplate);
            }
            return;
        }

        if (next?.kind === "unit") {
            const unit = units.find((item) => item.id === next.id);
            if (unit) handleSelectRef(formatUnitIdRef(unit.id));
        }
    };

    const renderSelectedTarget = () => {
        if (targetOptions.length === 0) {
            return (
                <div className="border-t border-border/50 px-3 py-2 text-[10px] text-muted-foreground">
                    {t("referenceBrowser.noRefs")}
                </div>
            );
        }

        if (!selectedTargetInfo) {
            return (
                <div className="border-t border-border/50 px-3 py-2 text-[10px] text-muted-foreground">
                    {t("referenceBrowser.selectSourceFirst")}
                </div>
            );
        }

        if (selectedTargetInfo.kind === "scope") {
            return (
                <ScopeVariableBrowser
                    variable={selectedScopeVariable}
                    onSelect={handleSelectRef}
                />
            );
        }

        if (selectedTargetInfo.kind === "input") {
            return (
                <InputParamBrowser
                    initialParamName={selectedInputParamName}
                    inputVariables={inputScopeVars}
                    onSelect={handleSelectRef}
                />
            );
        }

        if (selectedTargetInfo.kind === "definition") {
            return (
                <DefinitionBrowser
                    definition={selectedDefinition}
                    onSelect={handleSelectRef}
                />
            );
        }

        if (selectedTargetInfo.kind === "unit") {
            return (
                <UnitIdentityBrowser
                    units={selectedUnit ? [selectedUnit] : units}
                    selectedUnitId={selectedTargetInfo.id}
                    onSelect={handleSelectRef}
                />
            );
        }

        if (selectedTargetInfo.kind === "asset") {
            const namespace = selectedTargetInfo.id.endsWith("/assets")
                ? selectedTargetInfo.id.slice(0, -"/assets".length)
                : undefined;
            return (
                <AssetBrowser
                    entries={assets}
                    namespace={namespace || undefined}
                    onSelect={handleSelectRef}
                />
            );
        }

        if (selectedTargetInfo.kind === "import-document") {
            return (
                <ImportDocumentBrowser
                    namespace={selectedTargetInfo.id}
                    snapshot={selectedImportDocument}
                    onSelect={handleSelectRef}
                />
            );
        }

        return (
            <ContentBrowser
                content={selectedContent}
                onSelect={handleSelectRef}
            />
        );
    };

    const content = (
        <div>
            <InteractiveRow
                className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 cursor-pointer"
                style={getScopeVariableStyle(currentColor, "surface")}
                onTrigger={() => setShowBrowser(!showBrowser)}
                expanded={showBrowser}
                hasPopup
            >
                {currentRefStr ? (
                    <>
                        <span
                            className="shrink-0 text-[10px] font-bold"
                            style={getScopeVariableStyle(currentColor)}
                        >
                            {getRefPrefix(currentRefStr)}
                        </span>
                        <span className="text-[11px] font-mono text-foreground/70 truncate">
                            {formatRefDisplay(currentRefStr)}
                        </span>
                    </>
                ) : (
                    <span className="text-[11px] italic text-muted-foreground">
                        {t("referenceBrowser.selectRef")}
                    </span>
                )}
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                    {showBrowser ? "▲" : "▼"}
                </span>
            </InteractiveRow>

            {hint ? (
                <p className="mt-1 px-0.5 text-[10px] text-muted-foreground">{hint}</p>
            ) : null}

            {showBrowser ? (
                <div className="mt-1.5 overflow-hidden rounded-md border border-border bg-background shadow-sm">
                    <div className="p-1.5">
                        <ComboSelect
                            value={selectedTarget}
                            onChange={handleTargetChange}
                            options={targetOptions}
                            placeholder={t("referenceBrowser.selectSource")}
                            allowClear={false}
                            size="sm"
                        />
                    </div>
                    {renderSelectedTarget()}
                </div>
            ) : null}
        </div>
    );

    if (inline) {
        return (
            <div className="w-full">
                {label ? (
                    <span className="text-[11px] font-semibold text-foreground/70 block px-0 py-1">
                        {label}
                    </span>
                ) : null}
                <div className="flex items-center gap-1">
                    <div className="flex-1 min-w-0">{content}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full">
            {label ? (
                <div className="flex items-center px-0 py-1.5 gap-2">
                    <span className="text-[11px] font-semibold text-foreground/70 flex-1">
                        {label}
                    </span>
                </div>
            ) : null}
            <div
                className={
                    borderless
                        ? ""
                        : "rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                }
            >
                {content}
            </div>
        </div>
    );
};
